import React from 'react';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';

import { Card, Stack, Typography } from 'common/components/material';
import { useRouter, useSession } from 'common/hooks';
import AppRoutesEnum from 'common/routes/AppRoutes.enum';

import {
  OBJECTIVE_CATEGORY_LABELS,
  OBJECTIVE_DATE_FORMAT,
  OBJECTIVE_TYPE_LABELS,
} from '../objectives.constants';
import { ObjectiveStatusEnum } from '../objectives.enum';
import { getOwnerDisplayName } from '../objectives.utils';
import { ObjectiveResponse } from '../types/objective.type';
import ObjectiveStatusChip from './objective-status-chip.component';

type ObjectiveCardProps = {
  objective: ObjectiveResponse;
  showCategoryAndType?: boolean;
  showOwner?: boolean;
  readOnly?: boolean;
  /** Runs after navigating away — lets a drawer close itself behind the user. */
  onNavigate?: () => void;
};

const ObjectiveCard: React.FC<ObjectiveCardProps> = ({
  objective,
  showCategoryAndType = true,
  showOwner = false,
  readOnly = false,
  onNavigate,
}) => {
  const { palette } = useTheme();
  const { goTo } = useRouter();
  const { account } = useSession();

  const {
    id,
    title,
    description,
    category,
    type,
    startDate,
    endDate,
    status,
    kras,
    milestones,
    editedDate,
    ownerId,
    ownerName,
  } = objective;

  const ownerDisplayName = getOwnerDisplayName(ownerId, ownerName, account?.profileId);

  const handleClick = () => {
    goTo(AppRoutesEnum.OBJECTIVES_DETAIL, { objectiveId: id });
    onNavigate?.();
  };

  const metaParts = [
    showOwner && ownerDisplayName ? `Owner: ${ownerDisplayName}` : null,
    showCategoryAndType ? OBJECTIVE_CATEGORY_LABELS.get(category as string) : null,
    showCategoryAndType ? OBJECTIVE_TYPE_LABELS.get(type as string) : null,
    `${moment(startDate).format(OBJECTIVE_DATE_FORMAT)} — ${moment(endDate).format(
      OBJECTIVE_DATE_FORMAT,
    )}`,
    showOwner ? null : `${kras?.length ?? 0} KRAs · ${milestones?.length ?? 0} Milestones`,
    showOwner ? null : `Updated ${moment(editedDate).format(OBJECTIVE_DATE_FORMAT)}`,
  ].filter(Boolean);

  return (
    <Card
      onClick={readOnly ? undefined : handleClick}
      sx={{
        p: 1.5,
        width: '100%',
        borderRadius: 1.5,
        cursor: readOnly ? 'default' : 'pointer',
        backgroundColor: palette.Container.Bg,
        border: `1px solid ${palette.Card.Stroke}`,
        ...(!readOnly && {
          '&:hover': {
            borderColor: palette.SelectionChips.Primary.BgDefault,
          },
        }),
      }}
    >
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Typography variant="body3" fontWeight={600} color={palette.Text.Headline}>
            {title}
          </Typography>

          <ObjectiveStatusChip status={status as ObjectiveStatusEnum} />
        </Stack>

        <Typography variant="caption2" color={palette.Text.SubtleText}>
          {metaParts.join(' · ')}
        </Typography>
      </Stack>
    </Card>
  );
};

export default ObjectiveCard;
